import {useTranslation} from "react-i18next";
import {useFormik} from "formik";
import * as yup from "yup";
import {useDeviceDriverCardDownloadMutation, useDeviceDriverCardsQuery} from "../../../app/api/api";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {Button, MenuItem, Stack, Tooltip} from "@mui/material";
import React from "react";
import {DriverCardField} from "../../../app/api/model/driverCard";
import OrganizationStageSelect from "../../../components/input/OrganizationStageSelect";
import {DeviceJobField} from "../../../app/api/model/device";
import {DeviceJobType} from "../../../app/api/model/deviceJob";
import FormSelectField from "../../../components/input/FormSelectField";
import {InfoOutlined as InfoIcon} from "@mui/icons-material";

interface OrganizationUpdateStageDialogProps {
    open: boolean;
    onClose: () => void;
    organizationId: number;
    deviceId: number;
}

export default function DeviceDriverCardDownloadDialog({
    open,
    onClose,
    organizationId,
    deviceId,
}: OrganizationUpdateStageDialogProps) {
    const {t} = useTranslation();
    const {data: driverCards} = useDeviceDriverCardsQuery({deviceId});
    const [downloadDriverCard] = useDeviceDriverCardDownloadMutation();

    const handleClose = () => {
        onClose?.();
        form.resetForm();
    };

    const getValidationSchema = () =>
        yup.object({
            [DeviceJobField.STAGE]: yup.string().required(t("validation_inputRequired")),
            [DeviceJobField.DRIVER_CARD_ID]: yup.string().required(t("validation_inputRequired")),
            [DeviceJobField.DRIVER_CARD_SLOT]: yup.number().required(t("validation_inputRequired")),
        });

    const form = useFormik({
        initialValues: {
            [DeviceJobField.STAGE]: "",
            [DeviceJobField.DRIVER_CARD_ID]: "",
            [DeviceJobField.DRIVER_CARD_SLOT]: 0,
        },
        validationSchema: getValidationSchema(),
        onSubmit: async (values) => {
            downloadDriverCard({
                deviceId,
                [DeviceJobField.STAGE]: values[DeviceJobField.STAGE],
                [DeviceJobField.TYPE]: DeviceJobType.DRIVER_CARD,
                [DeviceJobField.DRIVER_CARD_NUMBER]: values[DeviceJobField.DRIVER_CARD_ID],
                [DeviceJobField.DRIVER_CARD_SLOT]: values[DeviceJobField.DRIVER_CARD_SLOT],
            });
            handleClose();
        },
    });

    function handleSubmit() {
        form.handleSubmit();
    }
    return (
        <DefaultDialog
            size={DefaultDialogSize.MEDIUM}
            open={open}
            titleKey={"driverCardDownloadDialog_headline"}
            onClose={handleClose}
            actions={
                <Button onClick={handleSubmit} color="primary">
                    {t("action_download")}
                </Button>
            }
        >
            <Stack spacing={2} pt={1}>
                <OrganizationStageSelect
                    fieldName={DeviceJobField.STAGE}
                    label={t("generic_stage")}
                    form={form}
                    t={t}
                    requestParams={{organizationId}}
                    hideEmptyOption={true}
                />
                <Stack spacing={2} pt={1} direction={"row"}>
                    <FormSelectField
                        fieldName={DeviceJobField.DRIVER_CARD_ID}
                        form={form}
                        label={t("driverCardDownloadDialog_driverCard")}
                        value={form.values[DeviceJobField.DRIVER_CARD_ID]}
                        onChange={(e) => {
                            const driverCard = driverCards?.find(
                                (dc) => dc[DriverCardField.CARD_ID] === e.target.value
                            );
                            form.setFieldValue(DeviceJobField.DRIVER_CARD_ID, driverCard?.[DriverCardField.CARD_ID]);
                            form.setFieldValue(
                                DeviceJobField.DRIVER_CARD_SLOT,
                                driverCard?.[DriverCardField.SLOT] ?? 0
                            );
                        }}
                        error={
                            form.touched[DeviceJobField.DRIVER_CARD_ID] &&
                            Boolean(form.errors[DeviceJobField.DRIVER_CARD_ID])
                        }
                        disabled={driverCards && driverCards.length < 1}
                    >
                        {Object.values(driverCards ?? []).map((driverCard, index) => {
                            return (
                                <MenuItem value={driverCard[DriverCardField.CARD_ID]} key={`driverCardOption_${index}`}>
                                    {driverCard[DriverCardField.CARD_ID]}
                                </MenuItem>
                            );
                        })}
                    </FormSelectField>
                    {driverCards && driverCards.length < 1 && (
                        <Tooltip title={t("driverCardDownloadDialog_infoNoDriverCards")}>
                            <InfoIcon fontSize="large" style={{margin: "10px 0 0 10px"}} />
                        </Tooltip>
                    )}
                </Stack>
            </Stack>
        </DefaultDialog>
    );
}
